import { useEffect, useState } from "react";
import { CheckCircle2, MinusCircle, Plus, Send, Newspaper, Wrench } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { CHANGELOG, markChangelogAsSeen, type ChangelogEntry } from "@/lib/changelog";
import { cn } from "@/lib/cn";
import { ChangelogBroadcastDialog } from "./ChangelogBroadcastDialog";

// =============================================================================
// ChangelogDialog
// -----------------------------------------------------------------------------
// Dialog modal listant les versions de la plateforme par ordre antéchronologique.
// Strictement informatif (pas de mutation).
//
// Effet de bord : à l'ouverture, marque la dernière version du changelog comme
// "vue" via localStorage afin que le badge "nouveau" du bouton header
// disparaisse — c'est suffisant car l'utilisateur a vu la liste, on ne fait
// pas le distingo entrée par entrée.
// =============================================================================

interface ChangelogDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ChangelogDialog({ open, onClose }: ChangelogDialogProps) {
  // Marque comme vu dès l'ouverture
  useEffect(() => {
    if (open) markChangelogAsSeen();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="2xl"
      title={
        <span className="flex items-center gap-2">
          <Newspaper aria-hidden="true" className="size-5 text-brand" />
          Nouveautés de la plateforme
        </span>
      }
      description="Plateforme en version 1 · historique des évolutions du produit, dernière version en haut."
      footer={
        <Button variant="primary" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      <div className="space-y-8">
        {CHANGELOG.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line p-6 text-center text-sm text-ink-muted">
            Aucune version enregistrée pour le moment.
          </p>
        ) : (
          CHANGELOG.map((entry, index) => (
            <Entry key={entry.version} entry={entry} isLatest={index === 0} />
          ))
        )}
      </div>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
function Entry({ entry, isLatest }: { entry: ChangelogEntry; isLatest: boolean }) {
  // Le dialog de broadcast est disponible pour TOUTES les versions : il arrive
  // qu'on doive (re)notifier les administrateurs d'une version antérieure.
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  return (
    <article className="space-y-4">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line pb-3">
        <span
          className={cn(
            "font-display text-lg font-bold tracking-tight",
            isLatest ? "text-brand" : "text-ink",
          )}
        >
          v{entry.version}
        </span>
        {entry.title && <span className="text-base text-ink">{entry.title}</span>}
        {isLatest && (
          <span className="rounded-full bg-brand-muted/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
            Dernière version
          </span>
        )}
        <span className="ml-auto text-xs text-ink-subtle">{formatDate(entry.date)}</span>
      </header>

      {entry.summary && <p className="text-sm text-ink-muted">{entry.summary}</p>}

      <div className="space-y-4">
        <Section
          title="Ajouté"
          icon={<Plus className="size-4" />}
          tone="success"
          items={entry.added}
        />
        <Section
          title="Amélioré"
          icon={<Newspaper aria-hidden="true" className="size-4" />}
          tone="info"
          items={entry.improved}
        />
        <Section
          title="Corrigé"
          icon={<Wrench className="size-4" />}
          tone="warning"
          items={entry.fixed}
        />
        <Section
          title="Retiré"
          icon={<MinusCircle className="size-4" />}
          tone="danger"
          items={entry.removed}
        />
      </div>

      {/* Action de broadcast : disponible sur TOUTES les versions, car il
          arrive de devoir (re)notifier les administrateurs d'une version
          antérieure. Permet d'envoyer un email récapitulatif aux admin_aon. */}
      <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
        <Button
          variant="outline"
          size="sm"
          iconLeft={<Send className="size-3.5" />}
          onClick={() => setBroadcastOpen(true)}
        >
          Notifier les administrateurs
        </Button>
      </div>
      <ChangelogBroadcastDialog
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        entry={entry}
      />
    </article>
  );
}

// -----------------------------------------------------------------------------
const TONE_CLASSES: Record<"success" | "info" | "warning" | "danger", string> = {
  success: "bg-success/15 text-success",
  info: "bg-info/15 text-info",
  warning: "bg-warning/15 text-warning",
  danger: "bg-danger/15 text-danger",
};

function Section({
  title,
  icon,
  tone,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  tone: "success" | "info" | "warning" | "danger";
  items: string[] | undefined;
}) {
  if (!items || items.length === 0) return null;

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
        <span
          className={cn(
            "inline-flex size-5 items-center justify-center rounded-full",
            TONE_CLASSES[tone],
          )}
        >
          {icon}
        </span>
        {title}
      </h3>
      <ul className="space-y-1.5 pl-7">
        {items.map((item, i) => (
          <li
            key={i}
            className="relative pl-4 text-sm leading-relaxed text-ink before:absolute before:left-0 before:top-2.5 before:size-1 before:rounded-full before:bg-ink-subtle"
          >
            <CheckCircle2 className="hidden size-3.5 shrink-0" aria-hidden />
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

// -----------------------------------------------------------------------------
function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
